import type { SingleUnit } from '../../types/schema';
import type { tGroupedEquipment } from '../../types';
import { isUnitArchived } from '../pages-equipment/equipmentFieldUtils';
import { Card } from '../ui';

type InspectionFlowUnitPickerProps = {
    equipmentData: tGroupedEquipment;
    equipmentImageUrl?: string;
    resolveLocationName: (unit: SingleUnit) => string | null;
    onSelectUnit: (unit: SingleUnit) => void;
};

const unitDisplayName = (unit: SingleUnit): string => unit.name || 'Unit';

const unitSubtitle = (unit: SingleUnit, locationName: string | null): string | null => {
    if (locationName) {
        return locationName;
    }

    if (unit.description && unit.description.trim() !== unit.name?.trim()) {
        return unit.description;
    }

    return null;
};

export const InspectionFlowUnitPicker = ({
    equipmentData,
    equipmentImageUrl,
    resolveLocationName,
    onSelectUnit,
}: InspectionFlowUnitPickerProps) => {
    const units =
        equipmentData.units?.filter((unit): unit is SingleUnit => Boolean(unit?.id) && !isUnitArchived(unit)) ?? [];

    return (
        <div className="inspection-flow-unit-picker">
            <Card variant="card-lg" className="inspection-flow-unit-picker-intro card-pad-lg">
                <div className="inspection-flow-unit-picker-intro-row">
                    <div className="inspection-flow-summary-image-wrap">
                        {equipmentImageUrl ? (
                            <img src={equipmentImageUrl} alt="" className="inspection-flow-summary-image" />
                        ) : (
                            <div className="inspection-flow-summary-image-placeholder" aria-hidden="true" />
                        )}
                    </div>
                    <div className="inspection-flow-unit-picker-intro-text">
                        <h1 className="inspection-flow-summary-title">{equipmentData.name}</h1>
                        <p className="inspection-flow-unit-picker-lead">
                            Select a unit to view inspection options and start work.
                        </p>
                    </div>
                </div>
            </Card>

            <div className="inspection-flow-unit-picker-list" role="list">
                {units.map((unit) => {
                    const locationName = resolveLocationName(unit);
                    const subtitle = unitSubtitle(unit, locationName);

                    return (
                        <Card
                            key={unit.id}
                            variant="card-sm"
                            className="inspection-flow-unit-option card-pad-md"
                            role="listitem"
                            tabIndex={0}
                            onClick={() => onSelectUnit(unit)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onSelectUnit(unit);
                                }
                            }}
                        >
                            <span className="inspection-flow-unit-option-body">
                                <span className="inspection-flow-unit-option-title">{unitDisplayName(unit)}</span>
                                {subtitle && (
                                    <span className="inspection-flow-unit-option-meta">{subtitle}</span>
                                )}
                            </span>
                            <span className="inspection-flow-action-chevron" aria-hidden="true">
                                ›
                            </span>
                        </Card>
                    );
                })}
            </div>

            {units.length === 0 && (
                <Card variant="card-sm" className="inspection-flow-unit-picker-empty card-pad-md">
                    <p>This equipment category has no units yet. Add a unit on the Equipment page, then try again.</p>
                </Card>
            )}
        </div>
    );
};
